import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWebSocket } from "../../contexts/WebSocketContext.jsx";
import { apiJson } from "../../utils/api.js";
import { fmtDt, userTitle } from "../../utils/users.js";
import { useDebouncedValue } from "../../hooks/useDebouncedValue.js";
import { CrmRow } from "./CrmRow.jsx";
import { MessengerBadge } from "./MessengerBadge.jsx";
import { InlineBanner } from "./InlineBanner.jsx";
import { ListEmpty } from "./ListEmpty.jsx";
import { PageReveal } from "./PageReveal.jsx";

const PAGE_SIZE = 30;

function initialsFromName(name) {
  const s = String(name || "").trim();
  if (!s) return "—";
  const parts = s.split(/\s+/).filter(Boolean);
  const a = (parts[0] || "").slice(0, 1);
  const b = (parts[1] || "").slice(0, 1);
  return (a + b).toUpperCase();
}

export function PickClientModal({
  open,
  onClose,
  onPick,
  title = "Выбор клиента",
  excludeUserId = null,
  /** Как экран «Диалоги»: только чаты с перепиской, превью, непрочитанные, те же поля API */
  dialogsListMode = false,
}) {
  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 300);
  const { subscribe } = useWebSocket();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [actionErr, setActionErr] = useState(null);
  const [rowCtx, setRowCtx] = useState(null);
  const [rowCtxPos, setRowCtxPos] = useState(null);
  const rowCtxRef = useRef(null);
  const [markUnreadErr, setMarkUnreadErr] = useState(null);

  const scrollRef = useRef(null);
  const query = useMemo(() => qDebounced.trim(), [qDebounced]);
  const loadPageRef = useRef(null);

  const [sheetVisible, setSheetVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const closeRowCtx = useCallback(() => {
    setRowCtx(null);
    setRowCtxPos(null);
  }, []);

  async function loadPage({ reset }) {
    if (loading || loadingMore) return;
    if (reset) setLoading(true);
    else setLoadingMore(true);

    setError(null);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(reset ? 0 : offset),
      });
      if (dialogsListMode) params.set("dialogs_only", "1");
      if (query) params.set("q", query);
      const data = await apiJson(`/api/users?${params.toString()}`);
      const newItems = data.items || [];
      const newTotal = data.total || 0;
      setTotal(newTotal);
      if (reset) {
        setItems(newItems);
        setOffset(newItems.length);
      } else {
        setItems((prev) => [...prev, ...newItems]);
        setOffset((prev) => prev + newItems.length);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      if (reset) {
        setItems([]);
        setTotal(0);
        setOffset(0);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  loadPageRef.current = loadPage;

  useEffect(() => {
    if (open) {
      setIsExiting(false);
      setSheetVisible(false);
      const id = setTimeout(() => setSheetVisible(true), 20);
      return () => clearTimeout(id);
    }
    setSheetVisible(false);
    setIsExiting(true);
    const t = setTimeout(() => setIsExiting(false), 400);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setError(null);
    setActionErr(null);
    setMarkUnreadErr(null);
    closeRowCtx();
  }, [open, closeRowCtx]);

  useEffect(() => {
    if (!open) return;
    loadPage({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query, dialogsListMode]);

  useEffect(() => {
    if (!open) return;
    const unsub = subscribe((payload) => {
      if (payload.type === "new_message" && loadPageRef.current) {
        loadPageRef.current({ reset: true });
      }
    });
    return unsub;
  }, [open, subscribe]);

  useEffect(() => {
    if (!rowCtx) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeRowCtx();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rowCtx, closeRowCtx]);

  useEffect(() => {
    if (!rowCtx) return;
    const M = 8;
    const raf = requestAnimationFrame(() => {
      const el = rowCtxRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth || 0;
      const vh = window.innerHeight || 0;
      let x = Number(rowCtx.x) || 0;
      let y = Number(rowCtx.y) || 0;
      if (vw > 0) x = Math.max(M, Math.min(x, vw - rect.width - M));
      else x = Math.max(M, x);
      if (vh > 0) y = Math.max(M, Math.min(y, vh - rect.height - M));
      else y = Math.max(M, y);
      setRowCtxPos({ x, y });
    });
    return () => cancelAnimationFrame(raf);
  }, [rowCtx]);

  const markRowUnread = useCallback(async () => {
    if (!rowCtx) return;
    const crmId = rowCtx.crmUserId;
    closeRowCtx();
    setMarkUnreadErr(null);
    try {
      const data = await apiJson(
        `/api/messages/mark-unread?user_id=${encodeURIComponent(String(crmId))}`,
        { method: "POST" }
      );
      const hasUnread = Boolean(data?.has_unread);
      setItems((prev) => prev.map((it) => (it.id === crmId ? { ...it, has_unread: hasUnread } : it)));
    } catch (e) {
      setMarkUnreadErr(e instanceof Error ? e.message : String(e));
    }
  }, [rowCtx, closeRowCtx]);

  const visibleItems = useMemo(() => {
    if (excludeUserId == null || excludeUserId === "") return items;
    const ex = Number(excludeUserId);
    if (!Number.isFinite(ex)) return items;
    return items.filter((u) => Number(u.id) !== ex);
  }, [items, excludeUserId]);

  const hasMore = offset < total;

  function isNearBottom(el, threshold = 150) {
    if (!el) return false;
    return el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
  }

  const show = open || isExiting;
  if (!show) return null;

  return (
    <div className={`modal ${open && sheetVisible ? "" : "hidden"}`} id="pickClientModal">
      <div className="modalBackdrop" id="pickClientBackdrop" onClick={onClose} />
      <div className="modalSheet pickClientSheet">
        <div className="modalHeader">
          <span className="modalTitle">{title}</span>
          <button
            type="button"
            className="iconBtn modalCloseBtn"
            id="pickClientClose"
            title="Закрыть"
            aria-label="Закрыть"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <main className="chat" id="pickClientDialogsScreen">
          <div className="crmStickyStack">
            {markUnreadErr ? (
              <InlineBanner
                message={markUnreadErr}
                variant="error"
                onClose={() => setMarkUnreadErr(null)}
              />
            ) : null}
            {actionErr ? (
              <InlineBanner message={actionErr} variant="error" onClose={() => setActionErr(null)} />
            ) : null}
            <div className="crmToolbar">
              <div className="crmToolbarRow">
                <input
                  className="input crmSearchInput"
                  id="pickClientQ"
                  placeholder="Поиск: имя, телефон или id"
                  autoComplete="off"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") onClose?.();
                  }}
                />
              </div>
            </div>
          </div>

          <div className="section">
            <div
              className="crmListSurface"
              id="list"
              ref={scrollRef}
              onScroll={() => {
                const el = scrollRef.current;
                if (!el) return;
                if (hasMore && !loading && !loadingMore && isNearBottom(el)) {
                  loadPage({ reset: false });
                }
              }}
            >
              <PageReveal ready={!loading}>
                {error ? (
                  <ListEmpty message="Ошибка загрузки" />
                ) : !visibleItems.length ? (
                  <ListEmpty message={dialogsListMode ? "Нет диалогов по фильтрам" : "Нет клиентов по запросу"} />
                ) : (
                  <>
                    {visibleItems.map((u) => {
                      const tgId = u.telegram_id != null ? Number(u.telegram_id) : null;
                      const disabled = tgId == null || tgId <= 0;
                      return (
                        <CrmRow
                          key={String(u.id ?? u.telegram_id)}
                          barClass="good"
                          disabled={disabled}
                          nativeTitle={
                            disabled ? "У клиента нет Telegram ID — переслать в этот чат нельзя" : undefined
                          }
                          avatar={
                            <span className={`avatar ${u.has_unread ? "isUnread" : ""}`} aria-hidden="true">
                              {initialsFromName(userTitle(u))}
                            </span>
                          }
                          title={
                            <span className="crmRowTitleCluster">
                              <span className="crmRowTitleName">{userTitle(u)}</span>
                              <MessengerBadge messenger={u.messenger} />
                              {u.has_unread ? <span className="rowUnreadDot" title="Есть непрочитанные" /> : null}
                            </span>
                          }
                          right={fmtDt(u.last_message_at || u.first_contact_at)}
                          bottom={
                            <span className="crmMeta crmLastMessagePreview" title={u.last_message_preview || undefined}>
                              {u.last_message_preview || "Нет сообщений"}
                            </span>
                          }
                          onClick={
                            disabled
                              ? undefined
                              : () => {
                                  setActionErr(null);
                                  onPick?.({ id: u.id, telegram_id: tgId });
                                }
                          }
                          onContextMenu={
                            disabled
                              ? undefined
                              : (e) => {
                                  e.preventDefault();
                                  setMarkUnreadErr(null);
                                  setRowCtx({ x: e.clientX, y: e.clientY, crmUserId: u.id });
                                  setRowCtxPos({ x: e.clientX, y: e.clientY });
                                }
                          }
                        />
                      );
                    })}
                  </>
                )}
              </PageReveal>
            </div>
          </div>

          {rowCtx ? (
            <div
              className="msgCtxBackdrop"
              onClick={closeRowCtx}
              onContextMenu={(e) => {
                e.preventDefault();
                closeRowCtx();
              }}
            >
              <div
                className="crmListCtxMenu"
                ref={rowCtxRef}
                style={{ left: rowCtxPos?.x ?? rowCtx.x, top: rowCtxPos?.y ?? rowCtx.y }}
                role="menu"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="msgCtxItem crmListCtxItem"
                  role="menuitem"
                  onClick={() => {
                    void markRowUnread();
                  }}
                >
                  Пометить как непрочитанное
                </button>
              </div>
            </div>
          ) : null}
        </main>
      </div>
    </div>
  );
}
